import React from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// Import project images
import digitalLifeLessonsImg from "../assets/digital-life-lessons-to-learn.netlify.app_ (1).png";
import heroAppsImg from "../assets/hero-apps-oi.netlify.app_.png";
import communityCleanlinessImg from "../assets/community-cleanliness-issues-report.netlify.app.png";
import greenEarthImg from "../assets/sourov-green-earth.netlify.app_.png";
import greenNestImg from "../assets/sourov-green-nest.netlify.app_.png";
import shifaImg from "../assets/shifa-medi.vercel.app.png";
import prizeBondImg from "../assets/price-bond-checker-five.vercel.app.png";

const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  const projects = [
    {
      id: 1,
      title: "Prize Bond Checker",
      description:
        "A full-stack web application that automates prize bond result checking for Bangladeshi users. Users can save their bond numbers, and when an admin uploads the official result PDF, the system instantly checks all bonds and notifies winners via email.",
      image: prizeBondImg,
      liveUrl: "https://price-bond-checker-five.vercel.app/",
      technologies: [
        "React",
        "Node.js",
        "Express",
        "MongoDB",
        "Firebase Auth",
        "Nodemailer",
      ],
      gradient: "from-emerald-500 to-teal-600",
      category: "Full Stack Application",
    },
    {
      id: 2,
      title: "Digital Life Lessons Platform",
      description:
        "An interactive educational platform designed to teach essential digital life skills and lessons. Features user-friendly navigation, responsive design, and engaging content delivery for modern learners.",
      image: digitalLifeLessonsImg,
      liveUrl: "https://digital-life-lesson-client.vercel.app/",
      technologies: [
        "React",
        "JavaScript",
        "CSS3",
        "Responsive Design",
        "Educational Tech",
      ],
      gradient: "from-blue-500 to-purple-600",
      category: "Educational Platform",
    },
    {
      id: 3,
      title: "Community Cleanliness Reporter",
      description:
        "A civic engagement platform enabling citizens to report cleanliness issues in their community. Features real-time reporting, location tracking, and community collaboration tools.",
      image: communityCleanlinessImg,
      liveUrl: "https://community-cleanliness-client.vercel.app/",
      technologies: [
        "React",
        "Community Tech",
        "Reporting System",
        "Civic Engagement",
        "Social Impact",
      ],
      gradient: "from-green-500 to-blue-600",
      category: "Civic Platform",
    },
    {
      id: 4,
      title: "SHIFA – Telemedicine Platform",
      description:
        "A smart telemedicine platform that connects patients with doctors through AI-powered recommendations, real-time appointment booking, and secure video consultations. It also includes features like nearby hospital and ambulance search, medical records management, and an intuitive user experience for healthcare access.",
      image: shifaImg,
      liveUrl: "https://shifa-client.vercel.app/",
      technologies: [
        "Next.js",
        "React",
        "JavaScript",
        "Tailwind CSS",
        "Gemini AI",
        "WebRTC",
        "REST API",
        "Responsive Design",
      ],
      gradient: "from-green-500 to-emerald-600",
      category: "Healthcare Platform",
    },
    {
      id: 5,
      title: "Green Nest Eco Hub",
      description:
        "A comprehensive eco-living platform providing resources, tips, and tools for sustainable lifestyle choices. Combines modern web technology with environmental consciousness.",
      image: greenNestImg,
      liveUrl: "https://sourov-green-nest.vercel.app/",
      technologies: [
        "React",
        "Eco-Tech",
        "Sustainable Living",
        "Green Technology",
        "Environmental Solutions",
      ],
      gradient: "from-teal-500 to-green-600",
      category: "Sustainability Hub",
    },
    {
      id: 6,
      title: "Hero Apps Collection",
      description:
        "A comprehensive collection of utility applications showcasing various React components and functionalities. Demonstrates modern UI/UX principles with clean, intuitive interfaces.",
      image: heroAppsImg,
      liveUrl: "https://sourov-hero-oi.vercel.app/",
      technologies: [
        "React",
        "JavaScript",
        "Modern UI",
        "Component Library",
        "Utility Apps",
      ],
      gradient: "from-emerald-500 to-teal-600",
      category: "Utility Collection",
    },
    {
      id: 7,
      title: "Green Earth Initiative",
      description:
        "An environmental awareness platform promoting sustainable practices and eco-friendly solutions. Features interactive content, environmental tips, and green living resources.",
      image: greenEarthImg,
      liveUrl: "https://sourov-green-earth.netlify.app/",
      technologies: [
        "React",
        "Environmental Tech",
        "Sustainability",
        "Green Solutions",
        "Eco-Friendly",
      ],
      gradient: "from-green-400 to-emerald-600",
      category: "Environmental Platform",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const titleVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: {
      y: 60,
      opacity: 0,
      scale: 0.9,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const ProjectCard = ({ project, index }) => (
    <motion.div
      className="group relative w-full min-h-[18rem] flex flex-col rounded-2xl overflow-hidden shadow-xl transition-all duration-500 digital-card animated-gradient-card"
      style={{
        background:
          "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(0, 0, 0, 0.95) 100%)",
      }}
      variants={cardVariants}
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Corner Accents */}

      {/* Project Image */}
      <div className="h-24 overflow-hidden bg-black flex-shrink-0">
        <motion.img
          src={project?.image}
          alt={`${project?.title || "Project"} Screenshot`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110 brightness-75 group-hover:brightness-100"
          variants={imageVariants}
          loading="lazy"
        />
        <div className="absolute top-2 left-2 max-w-[70%]">
          <span className="px-2 py-0.5 text-[9px] font-bold text-cyan-300 bg-black/70 backdrop-blur-sm rounded-full border border-cyan-500/40 font-mono uppercase tracking-wider inline-block leading-tight truncate">
            {project?.category}
          </span>
        </div>
        <div className="absolute top-2 right-2 w-7 h-7 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xs border border-cyan-400/50 shadow-lg">
          {String(index + 1).padStart(2, "0")}
        </div>
      </div>

      {/* Compact content section */}
      <div className="p-2.5 flex flex-col flex-1">
        <h3 className="text-sm font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-400 line-clamp-1 mb-1 leading-snug">
          {project?.title}
        </h3>

        <p className="text-gray-300 text-[11px] leading-relaxed line-clamp-2 mb-2 min-h-[2.4rem]">
          {project?.description}
        </p>

        <div className="flex flex-wrap gap-1 mb-1.5">
          {project?.technologies?.slice(0, 2).map((tech, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 text-[9px] font-mono font-semibold text-cyan-300 bg-cyan-500/10 border border-cyan-500/40 rounded-md uppercase whitespace-nowrap"
            >
              {tech}
            </span>
          ))}
          {project?.technologies?.length > 2 && (
            <span className="px-1.5 py-0.5 text-[9px] font-mono font-semibold text-purple-300 bg-purple-500/10 border border-purple-500/40 rounded-md">
              +{project.technologies.length - 2}
            </span>
          )}
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent mb-1.5" />

        <a
          href={project?.liveUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="animated-gradient-btn mt-auto inline-flex items-center justify-center w-full px-2.5 py-1.5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-pink-500 text-white text-[11px] font-bold rounded-lg transition-all duration-300 whitespace-nowrap"
        >
          <span className="mr-1.5 font-mono">LAUNCH</span>
          <i className="fas fa-rocket text-[10px]" />
        </a>
      </div>
    </motion.div>
  );

  return (
    <>
      {/* <style>{borderAnimationStyles}</style> */}
      <motion.section
        ref={ref}
        className="py-20 px-4 bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-background-dark dark:via-gray-900 dark:to-purple-900/10 relative overflow-hidden"
        id="projects"
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 -left-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl"
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
              transition: {
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
          <motion.div
            className="absolute bottom-1/4 -right-20 w-60 h-60 bg-secondary/10 rounded-full blur-3xl"
            animate={{
              x: [0, -30, 0],
              y: [0, 20, 0],
              transition: {
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              },
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            variants={containerVariants}
          >
            <motion.h2
              className="text-4xl md:text-5xl font-display font-bold mb-4"
              variants={titleVariants}
            >
              <motion.span
                className="text-primary neon-text"
                whileHover={{ scale: 1.05 }}
              >
                Featured
              </motion.span>{" "}
              <motion.span
                className="text-secondary neon-text-pink"
                whileHover={{ scale: 1.05 }}
              >
                Projects
              </motion.span>
            </motion.h2>

            <motion.div
              className="h-1 w-32 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mb-6"
              initial={{ width: 0 }}
              animate={isInView ? { width: "8rem" } : { width: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
            />

            <motion.p
              className="text-gray-500 dark:text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed"
              variants={titleVariants}
            >
              A showcase of my recent work and projects that demonstrate my
              skills in
              <span className="text-primary font-semibold">
                {" "}
                modern web development
              </span>{" "}
              and
              <span className="text-secondary font-semibold">
                {" "}
                user experience design
              </span>
            </motion.p>
          </motion.div>

          {/* Projects Grid */}
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 items-start"
            variants={containerVariants}
          >
            {projects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </motion.div>

          {/* Call to Action */}
          <motion.div className="text-center mt-16" variants={titleVariants}>
            <motion.div
              className="inline-block bg-white/80 dark:bg-card-dark/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 dark:border-white/10 shadow-lg"
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <motion.h3
                className="text-2xl font-display font-bold mb-4 text-gray-800 dark:text-white"
                whileHover={{ scale: 1.05 }}
              >
                Interested in My Work?
              </motion.h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md">
                These projects represent my journey in web development. I'm
                always excited to discuss new opportunities and collaborations!
              </p>
              <motion.button
                className="animated-gradient-btn px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-full transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  document
                    .getElementById("contact")
                    .scrollIntoView({ behavior: "smooth" })
                }
              >
                <i className="fas fa-envelope mr-2"></i>
                Let's Work Together
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default Projects;
